import type { HintKey } from 'constants/hints';
import type { UserProperties } from 'redux/modules/user.types';
import type { Hint, HintMeta } from 'types/hint';

/**
 * A hint can 'be seen' if there is a userProperty with the same key
 *
 * @param {Object} hintKey
 * @param  {Object} userProperties
 * @return {Boolean} does userProperties have that key?
 */
export const hasBeenSeen = (key: HintKey, userProperties: UserProperties): boolean => {
  return !!userProperties[key];
};

/**
 * A hint with a 'seenVia' attribute can be marked as seen if
 * any of those keys show up in userProperties
 *
 * @param {Object} hint An object that can have a seenVia: []
 * @param  {Object} userProperties
 * @return {Boolean} has it been seen?
 */
export const hasBeenSeenVia = (hint: Hint, userProperties: UserProperties): boolean => {
  const keys = hint.seenVia || [];
  return keys.some(key => hasBeenSeen(key, userProperties));
};

/**
 * A hint with a 'hideUntil' attribute can be marked as not seen
 * until all of it's masks have been fulfilled
 *
 * @param {Object} hint An object that can have a hideUntil: []
 * @param  {Object} userProperties
 * @return {Boolean} have all the masking properties been fulfilled?
 */
export const shouldBeHidden = (hint: Hint, userProperties: UserProperties): boolean => {
  const keys = hint.hideUntil || [];
  const unlocked = keys.every(key => hasBeenSeen(key, userProperties));
  return !unlocked;
};

/**
 * decides whether to show a hint based on:
 * hasBeenSeen
 * hasBeenSeenVia
 * shouldBeHidden.
 *
 * @param {Object} hint
 * @param  {Object} userProperties
 * @param  {Object} hintMeta
 * @return {Boolean} should it be shown?
 */
export const shouldShowHint = (
  key: HintKey,
  userProperties: UserProperties,
  hintMeta: Partial<HintMeta>
) => {
  const hint = hintMeta[key];
  if (!hint) {
    return false;
  }
  if (hasBeenSeen(key, userProperties)) {
    return false;
  }
  if (hasBeenSeenVia(hint, userProperties)) {
    return false;
  }
  if (shouldBeHidden(hint, userProperties)) {
    return false;
  }

  // Passes all checks, show this mofo!
  return true;
};
